import * as React from "react";
import { SettingsWindowMobile } from "../../../../ui/SettingsWindowMobile";
import { SettingsWindowsCloseButtonMobile } from "../../../../ui/SettingsWindowsCloseButtonMobile";
import { GameRules } from "../../../../v2/components/GameRules";
import { E2EIdentifier } from "../../../e2e/E2EIdentifier";
import { MenuTabs } from "../Common/MenuTabs";
import { PaytableMobile } from "../../../../v2/components/Paytable/PaytableMobile";
import {
  ExternalPaneButton,
  ExternalPaneDescription,
  ExternalPaneTitle,
  ExternalPaneWrapper
} from "./components/ExternalPane";
import {
  GameSettingsLabel,
  GameSettingsRow,
  GameSettingsTitle,
  GameSettingsWrapper,
  SettingItemWrapper,
  StyledSettingToggle
} from "./components/GameSettings";
import { Indicator } from "./components/Indicator";
import { MenuNav } from "./components/MenuNav";
import { SettingsContent } from "./components/SettingsContent";
import { SettingsPane } from "./components/SettingsPane";
import { StyledMenuNavButton } from "./components/StyledMenuNavButton";
import { IMenuMobile } from "./MenuMobile";
import { View } from "../../../../api/api";

const generateGameSettings = ({
  gameSettings,
  createTriggerSettingChangeRequest,
  t
}) => {
  return gameSettings.map((s, i) => {
    const Setting = (
      <StyledSettingToggle
        onToggle={createTriggerSettingChangeRequest(s.key, !s.value)}
        checked={s.value}
        dataE2EId={`${E2EIdentifier.GAME_SETTINGS_TOGGLE}-${s.key}`}
      />
    );
    return (
      <GameSettingsRow key={i}>
        <GameSettingsLabel
          checked={s.value}
          onClick={createTriggerSettingChangeRequest(s.key, !s.value)}
        >
          {s.name}
        </GameSettingsLabel>
        <SettingItemWrapper>{Setting}</SettingItemWrapper>
      </GameSettingsRow>
    );
  });
};

export const MenuMobileDefaultView = ({
  view,
  selectedTab,
  gameRulesAvailable,
  gameHistoryAvailable,
  gameHistoryEnabled,
  gameSettings,
  triggerDefaultViewChangeRequest,
  triggerMainSettingsViewChangeRequest,
  triggerGameRulesViewChangeRequest,
  triggerGameHistoryViewChangeRequest,
  createTriggerSettingChangeRequest,
  triggerPaytableViewChangeRequest,
  t
}: IMenuMobile) => {
  const menuViews = [
    View.MAIN_SETTINGS,
    View.GAME_HISTORY,
    View.GAME_RULES,
    View.PAYTABLE
  ];
  const isOpen = menuViews.includes(view);

  if (!isOpen) {
    return null;
  }

  const isGameSettingsActive = selectedTab === MenuTabs.SETTINGS;
  const isGameRulesActive = selectedTab === MenuTabs.RULES;
  const isGameHistoryActive = selectedTab === MenuTabs.HISTORY;
  const isPaytableActive = selectedTab === MenuTabs.PAYTABLE;

  let index = 0;

  const numOfTabs = [gameRulesAvailable, gameHistoryAvailable].reduce(
    (tabs: number, available: boolean) => (!available ? tabs - 1 : tabs),
    4
  );

  return (
    <SettingsWindowMobile open={isOpen}>
      <MenuNav>
        <StyledMenuNavButton
          data-e2e-id={E2EIdentifier.MENU_PAYTABLE_BUTTON}
          type={MenuTabs.PAYTABLE}
          index={index++}
          onClick={triggerPaytableViewChangeRequest}
          active={isPaytableActive}
          numOfTabs={numOfTabs}
        />
        <StyledMenuNavButton
          data-e2e-id={E2EIdentifier.MENU_GAME_SETTINGS_BUTTON}
          type={MenuTabs.SETTINGS}
          onClick={triggerMainSettingsViewChangeRequest}
          active={isGameSettingsActive}
          index={index++}
          numOfTabs={numOfTabs}
        />
        {gameRulesAvailable && (
          <StyledMenuNavButton
            data-e2e-id={E2EIdentifier.MENU_GAME_RULES_BUTTON}
            type={MenuTabs.RULES}
            onClick={triggerGameRulesViewChangeRequest}
            active={isGameRulesActive}
            index={index++}
            numOfTabs={numOfTabs}
          />
        )}
        {gameHistoryAvailable && (
          <StyledMenuNavButton
            data-e2e-id={E2EIdentifier.MENU_GAME_HISTORY_BUTTON}
            type={MenuTabs.HISTORY}
            onClick={triggerGameHistoryViewChangeRequest}
            active={isGameHistoryActive}
            index={index++}
            numOfTabs={numOfTabs}
          />
        )}
        <Indicator numOfTabs={numOfTabs} />
      </MenuNav>
      <SettingsContent>
        <SettingsPane
          dataE2EId={E2EIdentifier.GAME_SETTINGS_PAGE}
          active={isGameSettingsActive}
        >
          <GameSettingsWrapper>
            <GameSettingsTitle>{t("GameSettingsTitle")}</GameSettingsTitle>
            {generateGameSettings({
              gameSettings,
              createTriggerSettingChangeRequest,
              t
            })}
          </GameSettingsWrapper>
        </SettingsPane>
        <SettingsPane active={isPaytableActive}>
          <ExternalPaneWrapper>
            <PaytableMobile />
          </ExternalPaneWrapper>
        </SettingsPane>
        <SettingsPane
          dataE2EId={E2EIdentifier.GAME_RULES_PAGE}
          active={isGameRulesActive}
        >
          <ExternalPaneWrapper>
            <ExternalPaneTitle>{t("GameRulesTitle")}</ExternalPaneTitle>
            <GameRules />
          </ExternalPaneWrapper>
        </SettingsPane>
        <SettingsPane
          dataE2EId={E2EIdentifier.GAME_HISTORY_PAGE}
          active={isGameHistoryActive}
        >
          <ExternalPaneWrapper>
            <ExternalPaneTitle>{t("GameHistoryTitle")}</ExternalPaneTitle>
            <ExternalPaneDescription
              content={
                gameHistoryEnabled
                  ? t("GameHistoryDescriptionAvailable")
                  : t("GameHistoryDescriptionUnavailable")
              }
            />
            <ExternalPaneButton
              data-e2e-id={E2EIdentifier.GAME_HISTORY_BUTTON}
              apiName="gamehistorybutton"
              disabled={!gameHistoryEnabled}
              label={t("GameHistoryButton")}
            />
          </ExternalPaneWrapper>
        </SettingsPane>
      </SettingsContent>
      <SettingsWindowsCloseButtonMobile
        onClose={triggerDefaultViewChangeRequest}
      />
    </SettingsWindowMobile>
  );
};
